import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { resizeImage, ACCEPT_IMAGES } from '../../images.js';
import { PRIMARY_BUSINESS, ANNUAL_SALES, EMPLOYEES, MARKETS, PROVINCES, SOCIAL, placeLabel } from '../../companyOptions.js';
import { useCategoryTree, nodeName } from '../../categoryTree.js';
import { Alert, Field } from '../../components/ui.jsx';
import { ChipChoice, PlacePicker } from '../../components/MultiPick.jsx';
import RichTextEditor from '../../components/RichTextEditor.jsx';
import { AccountHead, useAccount } from './AccountLayout.jsx';
import { useCompanyForm } from './companyForm.js';
import ReviewNotice from './ReviewNotice.jsx';

const THIS_YEAR = new Date().getFullYear();

// Profil de l'entreprise: logo and cover photo (saved at once), then the
// company details, saved to review (the public page changes once an admin
// approves). Fields marked 🔒 never appear publicly.
export default function CompanyPage() {
  const { t, lang, errorText } = useI18n();
  const { user, refresh } = useAuth();
  const { reload } = useAccount();
  usePageTitle('accountNav.company');
  const c = useCompanyForm();
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [imgBusy, setImgBusy] = useState('');
  if (!c.form) return c.status.text ? <Alert>{errorText({ code: c.status.code })}</Alert> : <p className="muted">{t('common.loading')}</p>;
  const { form, set, update, locked, data } = c;
  const missing = new Set(data.missing);
  const req = (f) => (missing.has(f) ? 'is-missing' : undefined);
  const privateTag = <span className="private-tag">🔒 {t('company.private')}</span>;

  async function uploadImage(kind, file) {
    if (!file) return;
    setImgBusy(kind); setMsg({ kind: '', text: '' });
    try {
      const blob = await resizeImage(file, kind === 'logo' ? 600 : 1800);
      const fd = new FormData();
      fd.append(kind, blob, `${kind}.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`);
      const p = await api(`/profile/${kind}`, { method: 'POST', form: fd });
      c.setData((d) => ({ ...d, logoUrl: p.logoUrl, coverUrl: p.coverUrl }));
      refresh().catch(() => {}); reload();
      setMsg({ kind: 'success', text: t(`companyPage.${kind}Saved`) });
    } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); } finally { setImgBusy(''); }
  }
  async function removeCover() {
    try { const p = await api('/profile/cover', { method: 'DELETE' }); c.setData((d) => ({ ...d, coverUrl: p.coverUrl })); } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); }
  }
  async function submit(e) {
    e.preventDefault();
    const r = await c.save();
    if (r.error) { setMsg({ kind: 'error', text: errorText(r.err || { code: r.error }) }); return; }
    setMsg(r.result === 'unchanged' ? { kind: 'info', text: t('company.noChanges') } : { kind: 'success', text: t('companyPage.sent') });
    reload();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="acc-page">
      <AccountHead place="acc.company" title={t('accountNav.company')} lead={t('companyPage.lead')}
        actions={user.slug && user.isSeller ? <a className="btn btn-ghost" href={`/sellers/${user.slug}`} target="_blank" rel="noreferrer">{t('companyPage.viewPublic')} ↗</a> : null} />
      <ReviewNotice review={data.review} />
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>

      <section className="card">
        <h2 className="acc-card-title">{t('companyPage.images')}</h2>
        <div className="acc-media-row">
          <div className="field">
            <span className="field-label">{t('profile.logo')}</span>
            {data.logoUrl ? <img className="seller-logo seller-logo-lg" src={data.logoUrl} alt="" /> : <span className="seller-logo seller-logo-lg seller-logo-empty" aria-hidden="true">{(form.trade_name || form.legal_name || user.name).slice(0, 1)}</span>}
            <label className={`btn btn-ghost btn-sm${imgBusy === 'logo' ? ' is-disabled' : ''}`}>
              {imgBusy === 'logo' ? t('common.saving') : data.logoUrl ? t('profile.changeLogo') : t('profile.uploadLogo')}
              <input type="file" accept={ACCEPT_IMAGES} className="sr-only" onChange={(e) => { uploadImage('logo', e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>
          <div className="field">
            <span className="field-label">{t('companyPage.cover')}</span>
            <div className="acc-cover">
              {data.coverUrl && <img src={data.coverUrl} alt="" />}
              <label className={`btn btn-ghost btn-sm${imgBusy === 'cover' ? ' is-disabled' : ''}`}>
                {imgBusy === 'cover' ? t('common.saving') : data.coverUrl ? t('companyPage.changeCover') : t('companyPage.addCover')}
                <input type="file" accept={ACCEPT_IMAGES} className="sr-only" onChange={(e) => { uploadImage('cover', e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
            <p className="field-hint">{t('companyPage.coverHint')} {data.coverUrl && <button type="button" className="btn-link inline-link" onClick={removeCover}>{t('companyPage.removeCover')}</button>}</p>
          </div>
        </div>
      </section>

      <form onSubmit={submit} noValidate className="acc-page">
        <fieldset disabled={locked} className="company-fieldset acc-page">
          <section className="card form">
            <h2 className="acc-card-title">{t('company.sections.identity')}</h2>
            <div className="grid-form">
              <Field label={t('company.fields.legal_name')} htmlFor="co-legal"><input id="co-legal" className={req('legal_name')} value={form.legal_name} onChange={set('legal_name')} maxLength={200} /></Field>
              <Field label={t('company.fields.trade_name')} optional hint={t('company.tradeNameHint')} htmlFor="co-trade"><input id="co-trade" value={form.trade_name} onChange={set('trade_name')} maxLength={160} /></Field>
            </div>
            <div className="grid-form grid-3">
              <Field label={t('companyPage.type')} optional htmlFor="co-pb">
                <select id="co-pb" value={form.primary_business} onChange={set('primary_business')}>
                  <option value="">{t('profile.choose')}</option>
                  {PRIMARY_BUSINESS.map((b) => <option key={b} value={b}>{t(`company.business.${b}`)}</option>)}
                </select>
              </Field>
              <Field label={t('company.fields.year_established')} optional htmlFor="co-year">
                <input id="co-year" type="number" inputMode="numeric" min="1800" max={THIS_YEAR} value={form.year_established} onChange={set('year_established')} placeholder={String(THIS_YEAR - 10)} />
              </Field>
              <Field label={t('company.fields.employees')} optional htmlFor="co-emp">
                <select id="co-emp" value={form.employees} onChange={set('employees')}>
                  <option value="">{t('profile.choose')}</option>
                  {EMPLOYEES.map((x) => <option key={x} value={x}>{t(`company.employees.${x}`)}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid-form">
              <Field label={<>{t('companyPage.businessNumber')} {privateTag}</>} hint={t('company.businessNumberHint')} htmlFor="co-bn"><input id="co-bn" className={req('business_number')} value={form.business_number} onChange={set('business_number')} maxLength={60} /></Field>
              <Field label={<>{t('company.fields.annual_sales')} {privateTag}</>} optional htmlFor="co-sales">
                <select id="co-sales" value={form.annual_sales} onChange={set('annual_sales')}>
                  <option value="">{t('profile.choose')}</option>
                  {ANNUAL_SALES.map((x) => <option key={x} value={x}>{t(`company.sales.${x}`)}</option>)}
                </select>
              </Field>
            </div>
            <div className="field">
              <label htmlFor="co-desc">{t('company.fields.description')}</label>
              <RichTextEditor id="co-desc" value={form.description_html} onChange={(v) => update({ description_html: v })} invalid={missing.has('description')} disabled={locked} placeholder={t('companyPage.descriptionPlaceholder')} />
              <p className="field-hint">{t('company.descriptionHint')}</p>
            </div>
          </section>

          <section className="card form">
            <h2 className="acc-card-title">{t('companyPage.activity')}</h2>
            <SectorPicker value={form.sectors} onChange={(v) => update({ sectors: v })} disabled={locked} />
            <div className="grid-form grid-3">
              <Field label={t('company.fields.supplier_of')} optional htmlFor="co-sup"><textarea id="co-sup" rows={3} value={form.supplier_of} onChange={set('supplier_of')} maxLength={2000} placeholder={t('company.supplierPlaceholder')} /></Field>
              <Field label={t('company.fields.buyer_of')} optional htmlFor="co-buy"><textarea id="co-buy" rows={3} value={form.buyer_of} onChange={set('buyer_of')} maxLength={2000} placeholder={t('company.buyerPlaceholder')} /></Field>
              <Field label={t('company.fields.services')} optional htmlFor="co-svc"><textarea id="co-svc" rows={3} value={form.services} onChange={set('services')} maxLength={2000} placeholder={t('company.servicesPlaceholder')} /></Field>
            </div>
            <Field label={t('company.fields.certifications')} optional hint={t('company.certificationsHint')} htmlFor="co-cert"><input id="co-cert" value={form.certifications} onChange={set('certifications')} maxLength={500} placeholder="ISO 9001, HACCP" /></Field>
            <div className="grid-form grid-3">
              <Field label={t('company.fields.brand_name')} optional htmlFor="co-brand"><input id="co-brand" value={form.brand_name} onChange={set('brand_name')} maxLength={160} /></Field>
              <Field label={t('company.fields.division_name')} optional htmlFor="co-div"><input id="co-div" value={form.division_name} onChange={set('division_name')} maxLength={160} /></Field>
              <Field label={t('company.fields.brand_tagline')} optional htmlFor="co-tag"><input id="co-tag" value={form.brand_tagline} onChange={set('brand_tagline')} maxLength={200} /></Field>
            </div>
            <Field label={<>{t('company.fields.bankers')} {privateTag}</>} optional htmlFor="co-bank"><input id="co-bank" value={form.bankers} onChange={set('bankers')} maxLength={255} placeholder={t('company.bankersPlaceholder')} /></Field>
          </section>

          <section className="card form">
            <h2 className="acc-card-title">{t('companyPage.markets')}</h2>
            <div className="field">
              <span className="field-label">{t('company.fields.markets')} <span className="field-optional">({t('common.optional')})</span></span>
              <ChipChoice id="co-markets" label={t('company.fields.markets')} value={form.markets} onChange={(v) => update({ markets: v })} options={MARKETS.map((x) => ({ code: x, name: placeLabel(x, t, lang) }))} />
            </div>
            <div className="field">
              <span className="field-label">{t('company.fields.domestic_markets')} <span className="field-optional">({t('common.optional')})</span></span>
              <ChipChoice id="co-dom" label={t('company.fields.domestic_markets')} value={form.domestic_markets} onChange={(v) => update({ domestic_markets: v })} options={PROVINCES.map((x) => ({ code: x, name: t(`provinces.${x}`) }))} />
            </div>
            <div className="grid-form">
              <Field label={t('companyPage.exportMarkets')} optional htmlFor="co-exp"><PlacePicker id="co-exp" value={form.exported_to} onChange={(v) => update({ exported_to: v })} disabled={locked} /></Field>
              <Field label={t('company.fields.imported_from')} optional htmlFor="co-imp"><PlacePicker id="co-imp" value={form.imported_from} onChange={(v) => update({ imported_from: v })} disabled={locked} /></Field>
            </div>
          </section>

          <section className="card form">
            <h2 className="acc-card-title">{t('companyPage.online')}</h2>
            <Field label={t('company.fields.website')} optional htmlFor="co-web"><input id="co-web" type="url" inputMode="url" value={form.website} onChange={set('website')} placeholder="https://www.example.com" maxLength={255} /></Field>
            <div className="grid-form">
              {SOCIAL.map((k) => (
                <Field key={k} label={t(`company.social.${k}`)} optional htmlFor={`co-so-${k}`}>
                  <input id={`co-so-${k}`} type={k === 'wechat' ? 'text' : 'url'} value={form.social[k] || ''} maxLength={255}
                    onChange={(e) => update((f) => ({ social: { ...f.social, [k]: e.target.value } }))} placeholder={k === 'wechat' ? t('companyPage.wechatPlaceholder') : `https://${k === 'x' ? 'x' : k}.com/…`} />
                </Field>
              ))}
            </div>
          </section>
        </fieldset>

        {!locked && (
          <div className="acc-saved-bar">
            <p className="muted small">{c.dirty ? t('companyPage.unsaved') : t('company.submitHelp')}</p>
            <Link className="btn btn-ghost" to="/account/contact">{t('accountNav.contact')} →</Link>
            <button type="submit" className="btn btn-primary" disabled={c.busy}>{c.busy ? t('common.saving') : t('company.submit')}</button>
          </div>
        )}
      </form>
    </div>
  );
}

// Sectors served: any item of the category tree (search, or tick the
// sectors and categories listed).
function SectorPicker({ value, onChange, disabled }) {
  const { t, lang } = useI18n();
  const tree = useCategoryTree();
  const [q, setQ] = useState('');
  const chosen = value.map((id) => tree.get(id)).filter(Boolean);
  const list = useMemo(() => (q.trim().length >= 2
    ? tree.search(q, lang, 30).map((h) => h.node)
    : tree.roots.flatMap((r) => [r, ...tree.children(r.id)])), [q, tree, lang]);
  const toggle = (id) => onChange(value.includes(String(id)) ? value.filter((x) => x !== String(id)) : [...value, String(id)].slice(0, 30));
  return (
    <div className="field">
      <span className="field-label">{t('companyPage.sectors')} <span className="field-optional">({t('common.optional')})</span></span>
      <p className="field-hint">{t('companyPage.sectorsHint')}</p>
      {chosen.length > 0 && (
        <ul className="chip-list">
          {chosen.map((n) => <li key={n.id} className="chip">{tree.pathText(n.id, lang)}{!disabled && <button type="button" onClick={() => toggle(n.id)} aria-label={t('common.delete')}>×</button>}</li>)}
        </ul>
      )}
      {!disabled && (
        <>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('companyPage.sectorsSearch')} aria-label={t('companyPage.sectorsSearch')} />
          <div className="acc-tree-pick">
            {list.map((n) => (
              <label key={n.id} style={q.trim().length >= 2 ? undefined : { paddingLeft: n.parentId ? 26 : 6, fontWeight: n.parentId ? 400 : 600 }}>
                <input type="checkbox" checked={value.includes(String(n.id))} onChange={() => toggle(n.id)} />
                {q.trim().length >= 2 ? tree.pathText(n.id, lang) : nodeName(n, lang)}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
